/* co-anim — generic scroll-reveal engine.
   Add data-co-anim="fade-up|fade-left|fade-right|fade" to elements and data-co-anim-root to their
   section. When the section scrolls into view this adds .co-anim-in to each [data-co-anim] inside,
   with a staggered --co-anim-delay (DOM order, capped), and co-anim.css transitions them in. The
   flash-free pre-hide lives in co-anim.css behind the before-paint gate html.co-anim (set by
   snippets/co-anim-gate.liquid only when motion is allowed + IntersectionObserver exists + not the
   editor). Skipped here too in the editor + under reduced-motion → elements stay visible.
   FLICKITY-SAFE: data-co-anim belongs on an inner wrapper, never on a carousel cell. */
(function () {
  if (window.__coAnim) return;
  window.__coAnim = true;

  // engine is alive — cancel the gate's failsafe reveal
  if (window.__coAnimFailsafe) {
    clearTimeout(window.__coAnimFailsafe);
    window.__coAnimFailsafe = null;
  }

  var reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
  var STEP = 0.14; // seconds between staggered items
  var CAP = 8;    // cap so late items don't wait too long (off-screen carousel cells reveal soon)

  function reveal(root) {
    if (root.__coRevealed) return;
    root.__coRevealed = true;
    whenCarouselsReady(root, function () {
      /* Claim only [data-co-anim] elements that belong to THIS root — not ones nested inside
         another [data-co-anim-root] (e.g. a flower decoration's own root rendered inside a section
         root). Without this the nested element is revealed by BOTH roots, and the second reveal
         re-stamps a different --co-anim-delay onto the already-running keyframe (animation-fill-mode
         is backwards), snapping it back to 0% and replaying — a visible double entrance. The inner
         root reveals its own elements. */
      var items = Array.prototype.filter.call(
        root.querySelectorAll('[data-co-anim]'),
        function (node) {
          /* Anchor the search at the PARENT so a node that is itself both animated and a root
             (data-co-anim + data-co-anim-root on one element) is still owned by its ancestor root,
             not by itself — otherwise it would match neither this filter nor its own descendant-only
             reveal and stay hidden forever. */
          var owner = node.parentElement && node.parentElement.closest('[data-co-anim-root]');
          return owner === root;
        }
      );
      var steps = staggerSteps(root, items);
      for (var i = 0; i < items.length; i++) {
        items[i].style.setProperty('--co-anim-delay', (steps[i] * STEP).toFixed(2) + 's');
        items[i].classList.add('co-anim-in');
      }
    });
  }

  /* Per-item stagger step (0,1,2…, capped at CAP). Default = DOM order.
     Opt in to a different order on the root with data-co-anim-stagger (all viewports) or
     data-co-anim-stagger-mobile (only while data-co-anim-stagger-mobile-mq matches — default
     ≤749px — so desktop keeps DOM order):
       "center-out" — items whose horizontal centre is nearest the root's centre
                      reveal first, then outward symmetrically. For centre-aligned
                      carousels (cellAlign:center + wrapAround) where plain DOM
                      order ripples oddly (centre cell first; wrapped-left cells,
                      which are the highest DOM index, last). Measured from live
                      positions, so it reflects the carousel's on-screen layout. */
  function staggerSteps(root, items) {
    var n = items.length;
    var steps = new Array(n);
    /* Effective mode: the base attr, overridden by the mobile variant while its query matches.
       Resolved at reveal time, so it reflects the viewport the section actually enters view in. */
    var mode = root.getAttribute('data-co-anim-stagger');
    var mobileMode = root.getAttribute('data-co-anim-stagger-mobile');
    if (mobileMode) {
      var smq = root.getAttribute('data-co-anim-stagger-mobile-mq') || '(max-width: 749px)';
      if (window.matchMedia(smq).matches) mode = mobileMode;
    }
    if (mode === 'center-out' && n > 1) {
      var rootRect = root.getBoundingClientRect();
      var centre = rootRect.left + rootRect.width / 2;
      var ranked = [];
      for (var i = 0; i < n; i++) {
        var r = items[i].getBoundingClientRect();
        ranked.push({ i: i, d: Math.abs(r.left + r.width / 2 - centre) });
      }
      ranked.sort(function (a, b) { return a.d - b.d; });
      /* Group by distance so equidistant items (e.g. the left + right peeks of a centred carousel)
         share a step and reveal TOGETHER — a true symmetric ripple, not an arbitrary
         left-before-right order. ~4px tolerance absorbs sub-pixel asymmetry. */
      var step = 0;
      for (var k = 0; k < n; k++) {
        if (k > 0 && ranked[k].d - ranked[k - 1].d > 4) step++;
        steps[ranked[k].i] = step < CAP ? step : CAP;
      }
      return steps;
    }
    for (var j = 0; j < n; j++) steps[j] = j < CAP ? j : CAP;
    return steps;
  }

  /* Don't start the entrance while a Flickity carousel inside this section is still (re)building —
     it re-lays-out / re-parents the cells and would interrupt the animation (cards would jump in
     with no motion). Wait until every carousel here is enabled, with a 3s failsafe so a missing or
     failed carousel can never block the reveal forever. */
  function whenCarouselsReady(root, cb) {
    function pending() {
      var cs = root.querySelectorAll('[data-co-flickity]:not(.flickity-enabled)');
      var n = 0;
      for (var i = 0; i < cs.length; i++) {
        // A mobile-only carousel (data-co-flickity-mq) that doesn't match the current
        // viewport will never enable here — don't wait for it (e.g. desktop, where the
        // section is a static grid), or the reveal would stall until the failsafe.
        var mq = cs[i].getAttribute('data-co-flickity-mq');
        if (mq && !window.matchMedia(mq).matches) continue;
        n++;
      }
      return n;
    }
    if (!pending()) return cb();
    var tries = 0;
    var timer = setInterval(function () {
      if (!pending() || ++tries > 60) {
        clearInterval(timer);
        cb();
      }
    }, 50);
  }

  function init(root) {
    if (root.__coInit) return;
    root.__coInit = true;
    if (reduce.matches || typeof IntersectionObserver === 'undefined') return; // leave visible
    var io = new IntersectionObserver(
      function (entries, obs) {
        for (var i = 0; i < entries.length; i++) {
          if (entries[i].isIntersecting) {
            reveal(entries[i].target);
            obs.unobserve(entries[i].target);
          }
        }
      },
      // Reveal once the root's top edge scrolls a little into view. A plain `threshold: 0.15` can
      // NEVER be reached when a root is taller than ~6.7 viewports (e.g. a long product grid: its max
      // possible intersection ratio is viewport/rootHeight < 0.15) — so that section would never reveal
      // and stay hidden forever. Edge-entry (threshold 0 + a small bottom margin) works at any height.
      { threshold: 0, rootMargin: '0px 0px -10% 0px' }
    );
    io.observe(root);
  }

  function initAll(scope) {
    if (window.Shopify && window.Shopify.designMode) return; // no reveal in the editor
    (scope || document).querySelectorAll('[data-co-anim-root]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(document); });
  } else {
    initAll(document);
  }
  document.addEventListener('shopify:section:load', function (e) { initAll(e.target); });
})();
